<template>
    <div class="cluster-change-polygon-graph" ref="graphRef">
        <canvas ref="classAreaCanvasRef" class="class-area-canvas"></canvas>
        <!-- 클래스 영역 -->
        <canvas ref="backgroundCanvasRef" class="background-canvas"></canvas>
        <!-- 배경 라인 -->
        <canvas ref="polygonCanvasRef" class="polygon-canvas"></canvas>
        <!-- 포인트 / 폴리곤 영역 -->
    </div>
    <ul class="label-list">
        <li v-for="(areaInfo, index) in props.data.AreaInfos" :key="index" class="label-item-wrap">
            <div class="label-item" v-if="areaInfo.AreaClusterName || areaInfo.AreaBodyTypeName">
                <div class="label-color-chip" :style="{ backgroundColor: areaInfo.AreaColor ?? '' }"></div>
                <p class="label-text">
                    <span class="label-text-cluster-name" v-if="areaInfo.AreaClusterName">{{
                        areaInfo.AreaClusterName ?? ''
                    }}</span>
                    <span class="label-text-body-type-name" v-if="areaInfo.AreaBodyTypeName"
                        >({{ areaInfo.AreaBodyTypeName ?? '' }})</span
                    >
                </p>
            </div>
        </li>
    </ul>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import type { Ref } from 'vue';
import type { ClusterApproachGraph } from '@common/openapi-client/openapi';
import { computeConvexHull, hexToRgba, formatNumberToDecimal } from '@common/common/utils/graphUtils';

const props = defineProps<{
    data: ClusterApproachGraph;
    screenWidth: number;
    month: number | null;
    isForShare?: boolean;
}>();

const EPS = 1e-9;

const graphRef = ref<HTMLDivElement | null>(null);
const backgroundCanvasRef = ref<HTMLCanvasElement | null>(null);
const polygonCanvasRef = ref<HTMLCanvasElement | null>(null);
const classAreaCanvasRef = ref<HTMLCanvasElement | null>(null);

const imageCtx = ref<CanvasRenderingContext2D | null>(null);
const polygonCtx = ref<CanvasRenderingContext2D | null>(null);
const classAreaCtx = ref<CanvasRenderingContext2D | null>(null);

const originRatio = 226 / 365;

// isForShare이고 screenWidth가 560 초과일 때 너비 제한
const effectiveWidth = computed(() => {
    if (props.isForShare && props.screenWidth > 560) {
        return 560;
    }
    return props.screenWidth;
});

const width = ref(window.innerWidth);
const height = ref(width.value * originRatio);

const setInitData = async () => {
    if (!backgroundCanvasRef.value || !polygonCanvasRef.value || !classAreaCanvasRef.value) return;

    imageCtx.value = backgroundCanvasRef.value.getContext('2d', {
        willReadFrequently: true,
    });
    polygonCtx.value = polygonCanvasRef.value.getContext('2d', {
        willReadFrequently: true,
    });

    classAreaCtx.value = classAreaCanvasRef.value?.getContext('2d', {
        willReadFrequently: true,
    });

    const dpr = 3; // 고정 DPR 사용
    imageCtx.value?.setTransform(dpr, 0, 0, dpr, 0, 0);
    imageCtx.value?.scale(1 / dpr, 1 / dpr);

    polygonCtx.value?.setTransform(dpr, 0, 0, dpr, 0, 0);
    polygonCtx.value?.scale(1 / dpr, 1 / dpr);

    classAreaCtx.value?.setTransform(dpr, 0, 0, dpr, 0, 0);
    classAreaCtx.value?.scale(1 / dpr, 1 / dpr);

    setSize();

    await updateCanvas();
};

const setSize = (): void => {
    const dpr = 3;
    const canvasWidth = effectiveWidth.value;
    const canvasHeight = canvasWidth * originRatio;

    width.value = canvasWidth;
    height.value = canvasHeight;

    const setupCanvas = (canvas: HTMLCanvasElement | null, ctxRef: Ref<CanvasRenderingContext2D | null>) => {
        if (!canvas) return;
        canvas.width = canvasWidth * dpr;
        canvas.height = canvasHeight * dpr;
        canvas.style.width = `${canvasWidth}px`;
        canvas.style.height = `${canvasHeight}px`;

        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (ctx) {
            ctx.setTransform(1, 0, 0, 1, 0, 0); // 초기화
            ctx.scale(dpr, dpr); // ✅ 3배 확대 적용
        }
        ctxRef.value = ctx;
    };

    setupCanvas(backgroundCanvasRef.value, imageCtx);
    setupCanvas(polygonCanvasRef.value, polygonCtx);
    setupCanvas(classAreaCanvasRef.value, classAreaCtx);

    graphRef.value!.style.width = `${canvasWidth}px`;
    graphRef.value!.style.height = `${canvasHeight}px`;
};

const updateCanvas = async () => {
    if (!backgroundCanvasRef.value || !imageCtx.value) return;

    const ctx = imageCtx.value;
    const canvas = backgroundCanvasRef.value;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // 영역 색상 + 영역 텍스트 그리기
    drawClassArea();
    // 회색 라인
    drawBackground();
    // 폴리곤 영역
    drawPolygon();
    // 포인트 그리기 + 라벨 + 말풍선 그리기
    drawPoint();
};

const drawClassArea = () => {
    if (!classAreaCtx.value || !classAreaCanvasRef.value) return;

    const ctx = classAreaCtx.value;
    const canvasWidth = width.value;
    const canvasHeight = height.value;

    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    for (const area of props.data.AreaInfos ?? []) {
        const cond = area.AreaCondition?.[0];
        if (!cond) continue;

        const A = cond.ParameterA ?? 0;
        const B = cond.ParameterB ?? 0;
        const C = cond.ParameterC ?? 0;
        const isPositive = !!cond.IsPositive;

        // 반평면 잘라서 채우기
        const poly = clipRectByHalfPlane(canvasWidth, canvasHeight, A, B, C, isPositive);

        if (poly && poly.length >= 3) {
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(poly[0].x, poly[0].y);
            for (let i = 1; i < poly.length; i++) ctx.lineTo(poly[i].x, poly[i].y);
            ctx.closePath();
            ctx.fillStyle =
                area.AreaColor === '#F7F8FA'
                    ? 'transparent'
                    : (hexToRgba(area.AreaColor ?? '#FF0000', 0.5) ?? '#FF0000');
            ctx.fill();
            ctx.restore();
        }

        // 폴리곤이 그려졌다면, 중심에 AreaName 텍스트
        // if (poly && poly.length > 2 && area.AreaClusterName) {
        //     const centroid = getPolygonCentroid(poly);
        //     const canvasWidth = width.value;
        //     const canvasHeight = height.value;

        //     const lineHeight = 20;
        //     const font = '700 12px NotoSansKR, Arial, sans-serif';
        //     ctx.save();
        //     ctx.font = font;

        //     const bodyTypeText = area.AreaBodyTypeName ?? '';
        //     const clusterNameText = area.AreaClusterName ?? '';
        //     const textWidth = Math.max(ctx.measureText(bodyTypeText).width, ctx.measureText(clusterNameText).width);
        //     const pad = 8;

        //     // 2줄 전체 텍스트 박스
        //     const boxW = textWidth + pad * 2;
        //     const boxH = lineHeight * 2 + 2;

        //     // 캔버스 테두리에서 4px 띄운 안전 영역 계산
        //     const margin = 4;
        //     const safeLeft = margin;
        //     const safeRight = canvasWidth - margin;
        //     const safeTop = margin;
        //     const safeBottom = canvasHeight - margin;

        //     // 위치 후보들을 안전 영역 내에서 생성 (중앙을 마지막으로 배치)
        //     const positions = [
        //         // 캔버스 모서리 근처 위치들 (안전 영역 내)
        //         {
        //             x: Math.max(safeLeft + boxW / 2, Math.min(safeRight - boxW / 2, centroid.x)),
        //             y: safeTop + boxH / 2,
        //         }, // 상단
        //         {
        //             x: Math.max(safeLeft + boxW / 2, Math.min(safeRight - boxW / 2, centroid.x)),
        //             y: safeBottom - boxH / 2,
        //         }, // 하단
        //         {
        //             x: safeLeft + boxW / 2,
        //             y: Math.max(safeTop + boxH / 2, Math.min(safeBottom - boxH / 2, centroid.y)),
        //         }, // 좌측
        //         {
        //             x: safeRight - boxW / 2,
        //             y: Math.max(safeTop + boxH / 2, Math.min(safeBottom - boxH / 2, centroid.y)),
        //         }, // 우측
        //         // 중앙 근처 위치들
        //         { x: centroid.x, y: centroid.y - 48 }, // 위
        //         { x: centroid.x, y: centroid.y + 48 }, // 아래
        //         { x: centroid.x - 48, y: centroid.y }, // 좌
        //         { x: centroid.x + 48, y: centroid.y }, // 우
        //         { x: centroid.x, y: centroid.y }, // 중앙 (최후의 수단)
        //     ];

        //     // 캔버스 내부 체크 (안전 영역 고려)
        //     const isBoxInCanvas = (x: number, y: number, boxW: number, boxH: number) => {
        //         return (
        //             x - boxW / 2 >= safeLeft &&
        //             x + boxW / 2 <= safeRight &&
        //             y - boxH / 2 >= safeTop &&
        //             y + boxH / 2 <= safeBottom
        //         );
        //     };

        //     // area 영역 내부에 있는지 체크
        //     const isBoxInArea = (x: number, y: number, boxW: number, boxH: number) => {
        //         const corners = [
        //             { x: x - boxW / 2, y: y - boxH / 2 }, // 좌상
        //             { x: x + boxW / 2, y: y - boxH / 2 }, // 우상
        //             { x: x + boxW / 2, y: y + boxH / 2 }, // 우하
        //             { x: x - boxW / 2, y: y + boxH / 2 }, // 좌하
        //         ];

        //         // 텍스트 박스의 모든 모서리가 area 영역 내부에 있는지 확인
        //         return corners.every(corner => {
        //             const nx = corner.x / canvasWidth;
        //             const ny_math = 1 - corner.y / canvasHeight; // Y축 반전
        //             const v = A * nx + B * ny_math + C;
        //             return isPositive ? v >= -EPS : v <= EPS;
        //         });
        //     };

        //     // 위치와 글자 크기를 찾는 함수
        //     const findBestPosition = (fontSize: number) => {
        //         ctx.font = `700 ${fontSize}px NotoSansKR, Arial, sans-serif`;
        //         const bodyTypeWidth = ctx.measureText(bodyTypeText).width;
        //         const clusterNameWidth = ctx.measureText(clusterNameText).width;
        //         const maxWidth = Math.max(bodyTypeWidth, clusterNameWidth);
        //         const currentBoxW = maxWidth + pad * 2;
        //         const currentBoxH = lineHeight * 2 + 2;

        //         for (const pos of positions) {
        //             // 캔버스 안전 영역과 area 영역 모두 체크
        //             if (
        //                 isBoxInCanvas(pos.x, pos.y, currentBoxW, currentBoxH) &&
        //                 isBoxInArea(pos.x, pos.y, currentBoxW, currentBoxH)
        //             ) {
        //                 return { pos, boxW: currentBoxW, boxH: currentBoxH, fontSize };
        //             }
        //         }
        //         return null;
        //     };

        //     // 12px부터 시작해서 10px까지 시도
        //     let result = findBestPosition(12);
        //     if (!result) {
        //         result = findBestPosition(10);
        //     }

        //     // 그래도 안되면 중앙에 12px로 표시 (최후의 수단)
        //     if (!result) {
        //         result = {
        //             pos: positions[positions.length - 1], // 중앙
        //             boxW: boxW,
        //             boxH: boxH,
        //             fontSize: 12,
        //         };
        //     }

        //     ctx.textAlign = 'center';
        //     ctx.textBaseline = 'middle';
        //     ctx.fillStyle = '#fff';
        //     ctx.shadowColor = 'rgba(0,0,0,0.18)';
        //     ctx.shadowBlur = 2;
        //     ctx.font = `700 ${result.fontSize}px NotoSansKR, Arial, sans-serif`;

        //     ctx.fillText(bodyTypeText, result.pos.x, result.pos.y - lineHeight / 2);
        //     ctx.fillText(clusterNameText, result.pos.x, result.pos.y + lineHeight / 2);

        //     ctx.restore();
        // }

        // 하얀색 경계선 (math y↑ → canvas y↓ 변환 적용)
        const boundaryPoints = getBoundaryLineCrossPoints(A, B, C, canvasWidth, canvasHeight);
        if (boundaryPoints.length === 2) {
            const [p0, p1] = boundaryPoints;
            const dx = p1.x - p0.x;
            const dy = p1.y - p0.y;
            const len = Math.hypot(dx, dy) || 1;
            const extend = Math.max(canvasWidth, canvasHeight) * 2;

            const ex0 = {
                x: p0.x - (dx / len) * extend,
                y: p0.y - (dy / len) * extend,
            };
            const ex1 = {
                x: p1.x + (dx / len) * extend,
                y: p1.y + (dy / len) * extend,
            };

            ctx.save();
            ctx.beginPath();
            ctx.moveTo(ex0.x, ex0.y);
            ctx.lineTo(ex1.x, ex1.y);
            ctx.strokeStyle = '#fff';
            ctx.lineWidth = 6;
            ctx.shadowColor = 'rgba(0,0,0,0.08)';
            ctx.shadowBlur = 1;
            ctx.stroke();
            ctx.restore();
        }
    }
};

// 반평면 클리핑 (mathY 변환 + 패딩 포함)
const clipRectByHalfPlane = (
    canvasWidth: number,
    canvasHeight: number,
    A: number,
    B: number,
    C: number,
    isPositive: boolean,
) => {
    const padding = polygonPaddingRatio;

    const corners = [
        { x: 0, y: 0 },
        { x: canvasWidth, y: 0 },
        { x: canvasWidth, y: canvasHeight },
        { x: 0, y: canvasHeight },
    ];
    const edges = [
        [corners[0], corners[1]],
        [corners[1], corners[2]],
        [corners[2], corners[3]],
        [corners[3], corners[0]],
    ];

    // 캔버스 좌표를 패딩 적용된 정규화 좌표로 변환
    const inside = (x: number, y: number) => {
        const nx = (x / canvasWidth - padding) / (1 - 2 * padding);
        const ny_math = 1 - (y / canvasHeight - padding) / (1 - 2 * padding);
        const v = A * nx + B * ny_math + C;
        return isPositive ? v >= -EPS : v <= EPS;
    };

    const out: { x: number; y: number }[] = [];

    for (let i = 0; i < edges.length; i++) {
        const curr = edges[i][0];
        const next = edges[i][1];

        const cIn = inside(curr.x, curr.y);
        if (cIn) out.push({ ...curr });

        const dx = next.x - curr.x;
        const dy = next.y - curr.y;
        // 패딩 적용된 좌표계에서의 미분값
        const scale = 1 / (1 - 2 * padding);
        const denom = A * (dx / canvasWidth) * scale + B * (-dy / canvasHeight) * scale;
        if (Math.abs(denom) > EPS) {
            const nx0 = (curr.x / canvasWidth - padding) * scale;
            const ny0_math = 1 - (curr.y / canvasHeight - padding) * scale;
            const f0 = A * nx0 + B * ny0_math + C;
            const t = -f0 / denom;
            if (t > 0 && t < 1) {
                out.push({ x: curr.x + t * dx, y: curr.y + t * dy });
            }
        }
    }
    return out.length >= 3 ? out : null;
};

// 하얀 경계선 교점 계산 (mathY 변환 + 패딩 포함)
const getBoundaryLineCrossPoints = (A: number, B: number, C: number, canvasWidth: number, canvasHeight: number) => {
    const pts: { x: number; y: number }[] = [];
    const padding = polygonPaddingRatio;

    // 정규화 좌표 (0~1)을 캔버스 좌표로 변환하는 함수
    const toCanvasX = (nx: number) => (padding + (1 - 2 * padding) * nx) * canvasWidth;
    const toCanvasY = (ny_math: number) => (padding + (1 - 2 * padding) * (1 - ny_math)) * canvasHeight;

    // x=0 (정규화 좌표계 기준)
    if (Math.abs(B) > EPS) {
        const yMath = -C / B;
        if (yMath >= -EPS && yMath <= 1 + EPS) {
            pts.push({ x: toCanvasX(0), y: toCanvasY(yMath) });
        }
    }
    // x=1 (정규화 좌표계 기준)
    if (Math.abs(B) > EPS) {
        const yMath = -(A + C) / B;
        if (yMath >= -EPS && yMath <= 1 + EPS) {
            pts.push({ x: toCanvasX(1), y: toCanvasY(yMath) });
        }
    }
    // y=0 (정규화 좌표계 기준, math)
    if (Math.abs(A) > EPS) {
        const x = -C / A;
        if (x >= -EPS && x <= 1 + EPS) {
            pts.push({ x: toCanvasX(x), y: toCanvasY(0) });
        }
    }
    // y=1 (정규화 좌표계 기준, math)
    if (Math.abs(A) > EPS) {
        const x = -(B + C) / A;
        if (x >= -EPS && x <= 1 + EPS) {
            pts.push({ x: toCanvasX(x), y: toCanvasY(1) });
        }
    }

    return pts.length >= 2 ? pts.slice(0, 2) : [];
};

// function drawAreaPolygonByLine(
//     ctx: CanvasRenderingContext2D,
//     canvasWidth: number,
//     canvasHeight: number,
//     A: number,
//     B: number,
//     C: number,
//     isPositive: boolean,
//     fillColor: string,
// ) {
//     // 네모 꼭짓점(시계방향)
//     const corners = [
//         { x: 0, y: 0 },
//         { x: canvasWidth, y: 0 },
//         { x: canvasWidth, y: canvasHeight },
//         { x: 0, y: canvasHeight },
//     ];

//     // 네 변 정의 (p0-p1, p1-p2, p2-p3, p3-p0)
//     const edges = [
//         [corners[0], corners[1]],
//         [corners[1], corners[2]],
//         [corners[2], corners[3]],
//         [corners[3], corners[0]],
//     ];

//     const isInSide = (x: number, y: number) => {
//         const normX = x / canvasWidth;
//         const normY = y / canvasHeight;
//         const value = A * normX + B * normY + C;
//         return isPositive ? value >= 0 : value < 0;
//     };

//     let polygon: { x: number; y: number }[] = [];

//     for (let i = 0; i < 4; i++) {
//         const curr = edges[i][0];
//         const next = edges[i][1];

//         const currInside = isInSide(curr.x, curr.y);
//         if (currInside) polygon.push({ ...curr });

//         // 이 변과 직선의 교점 구하기
//         // edge: (x1, y1)-(x2, y2)
//         const dx = next.x - curr.x;
//         const dy = next.y - curr.y;
//         // Parametric t: 0~1
//         // x = curr.x + t*dx, y = curr.y + t*dy
//         // → (A*(x/canvasWidth) + B*(y/canvasHeight) + C = 0) 풀어서 t 구하기
//         const denom = A * (dx / canvasWidth) + B * (dy / canvasHeight);
//         if (denom !== 0) {
//             // t값 구함
//             const t = -(A * (curr.x / canvasWidth) + B * (curr.y / canvasHeight) + C) / denom;
//             if (t > 0 && t < 1) {
//                 // 교점 좌표
//                 const ix = curr.x + t * dx;
//                 const iy = curr.y + t * dy;
//                 polygon.push({ x: ix, y: iy });
//             }
//         }
//     }

//     if (polygon.length < 3) return;
//     ctx.beginPath();
//     ctx.moveTo(polygon[0].x, polygon[0].y);
//     for (let i = 1; i < polygon.length; i++) {
//         ctx.lineTo(polygon[i].x, polygon[i].y);
//     }
//     ctx.closePath();
//     ctx.fillStyle = fillColor;

//     ctx.fill();

//     // ★ 폴리곤 꼭짓점 배열 반환!
//     return polygon;
// }

// 폴리곤 중심(centroid) 계산 함수
// const getPolygonCentroid = (points: { x: number; y: number }[]): { x: number; y: number } => {
//     let x = 0,
//         y = 0,
//         n = points.length;
//     for (const pt of points) {
//         x += pt.x;
//         y += pt.y;
//     }
//     return { x: x / n, y: y / n };
// };

const drawBackground = (): void => {
    if (!imageCtx.value || !backgroundCanvasRef.value) return;

    const ctx = imageCtx.value;

    const canvasWidth = width.value;
    const canvasHeight = height.value;

    const numLines = 5;
    const lineSpacing = canvasHeight / numLines;

    ctx.save();
    ctx.strokeStyle = 'rgba(96, 105, 118, 0.1)'; // 회색 선 색상
    ctx.lineWidth = 1;

    for (let i = 0; i <= numLines; i++) {
        const y = lineSpacing * i;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvasWidth, y);
        ctx.stroke();
    }

    ctx.restore();
};

const polygonPaddingRatio = 0.25; // 10% padding

const scalePoint = (
    origX: number,
    origY: number,
    canvasWidth: number,
    canvasHeight: number,
    padding = polygonPaddingRatio,
) => {
    // 포인트 스케일 변환 함수
    const scaledX = padding + (1 - 2 * padding) * origX;
    // Y축 반전 적용: 1 - origY로 변환하여 클래스 영역과 좌표계 통일
    const scaledY = padding + (1 - 2 * padding) * (1 - origY);
    return {
        x: scaledX * canvasWidth,
        y: scaledY * canvasHeight,
    };
};

const drawPolygon = (): void => {
    if (!polygonCtx.value || !polygonCanvasRef.value) {
        return;
    }

    const ctx = polygonCtx.value;
    const canvasWidth = width.value;
    const canvasHeight = height.value;

    const points = props.data.PointDatas?.map(p => scalePoint(p.X ?? 0, p.Y ?? 0, canvasWidth, canvasHeight)) ?? [];

    const hull = computeConvexHull(points); // 외곽점만 계산

    if (!hull || hull.length < 3) {
        return;
    }

    ctx.save();
    ctx.fillStyle = 'rgba(199, 203, 209, 0.5)';
    ctx.strokeStyle = '#FF9900';
    ctx.lineWidth = 2;

    ctx.beginPath();
    hull.forEach((pt, i) => {
        i === 0 ? ctx.moveTo(pt.x, pt.y) : ctx.lineTo(pt.x, pt.y);
    });
    ctx.closePath();
    ctx.fill();
    ctx.restore();
};

const drawPoint = () => {
    if (!polygonCtx.value || !polygonCanvasRef.value) return;

    const ctx = polygonCtx.value;
    const canvasWidth = width.value;
    const canvasHeight = height.value;

    const radius = 2.5;

    let firstPoint = null;
    let firstPixelX = 0,
        firstPixelY = 0;

    let redDotPoint = null;
    let redPixelX = 0,
        redPixelY = 0;

    let averagePoint = null;
    let averagePixelX = 0,
        averagePixelY = 0;

    ctx.save();

    for (const [idx, point] of (props.data.PointDatas ?? []).entries()) {
        const { x: pixelX, y: pixelY } = scalePoint(point.X ?? 0, point.Y ?? 0, canvasWidth, canvasHeight);

        if (point.MarkColor === '#9c9798') {
            if (idx === 0) {
                // 맨 위에 그릴 점은 일단 저장만
                firstPoint = point;
                firstPixelX = pixelX;
                firstPixelY = pixelY;
                continue; // 바로 그리지 않음
            }

            // 일반 점
            ctx.save();
            ctx.fillStyle = point.IsAveragePoint ? '#21242E' : (point.MarkColor ?? 'rgba(33, 36, 46, 0.2)');
            ctx.beginPath();
            ctx.arc(pixelX, pixelY, radius, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();

            if (point.IsAveragePoint) {
                averagePoint = point;
                averagePixelX = pixelX;
                averagePixelY = pixelY;

                continue;
            }
        } else if (point.MarkColor === '#fa163c') {
            redDotPoint = point;
            redPixelX = pixelX;
            redPixelY = pixelY;

            // 1. 레드닷
            ctx.save();
            ctx.strokeStyle = '#fa163c';
            ctx.lineWidth = 3;
            ctx.fillStyle = '#fff';
            ctx.beginPath();
            ctx.arc(pixelX, pixelY, 5, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            ctx.restore();

            // 3. MeasureDate 텍스트 (아래 5px)
            if (point.MeasureDate) {
                ctx.save();
                ctx.font = 'bold 12px NotoSansKR, Arial, sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'top';
                ctx.fillStyle = '#606976';
                const splitDateStr = point.MeasureDate.split('T')[0].split('-');
                const dateText = `${parseInt(splitDateStr[1], 10)}월 ${parseInt(splitDateStr[2], 10)}일`;
                ctx.fillText(dateText, pixelX, pixelY + radius + 5);
                ctx.restore();
            }

            // 4. WT / PBF 텍스트
            if (point.WT != null && point.PBF != null) {
                ctx.save();
                ctx.font = 'normal 12px NotoSansKR, Arial, sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'top';
                ctx.fillStyle = '#606976';
                const label = `${formatNumberToDecimal(point.WT)}kg / ${formatNumberToDecimal(point.PBF)}%`;
                ctx.fillText(label, pixelX, pixelY + radius + 5 + 20);
                ctx.restore();
            }
        }
    }

    if (firstPoint) {
        // 첫번째 점 테두리
        ctx.save();
        ctx.lineWidth = 4.5;
        ctx.fillStyle = '#897B7B';
        ctx.beginPath();
        ctx.arc(firstPixelX, firstPixelY, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        // --- 3. MeasureDate 텍스트 (아래 5px)
        if (firstPoint.MeasureDate) {
            ctx.save();
            ctx.font = 'bold 12px NotoSansKR, Arial, sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            ctx.fillStyle = '#606976';
            const splitDateStr = firstPoint.MeasureDate.split('T')[0].split('-');
            const dateText = `${parseInt(splitDateStr[1], 10)}월 ${parseInt(splitDateStr[2], 10)}일`;
            ctx.fillText(dateText, firstPixelX, firstPixelY + radius + 5);
            ctx.restore();
        }

        // --- 4. WT / PBF 텍스트
        if (firstPoint.WT != null && firstPoint.PBF != null) {
            ctx.save();
            ctx.font = 'normal 12px NotoSansKR, Arial, sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            ctx.fillStyle = '#606976';
            const label = `${formatNumberToDecimal(firstPoint.WT)}kg / ${formatNumberToDecimal(firstPoint.PBF)}%`;
            ctx.fillText(label, firstPixelX, firstPixelY + radius + 5 + 20);
            ctx.restore();
        }
    }

    // 반지름(그려주는 점과 레드닷 반지름, 위에서 쓴 값)
    const firstRadius = 5; // 첫 점 반지름
    const redDotRadius = 5; // 레드닷 반지름

    if (firstPoint && redDotPoint) {
        // 두 점(원의 중심) 간 벡터
        const dx = redPixelX - firstPixelX;
        const dy = redPixelY - firstPixelY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        // "원의 경계에서 추가로 더 띄워서" 시작/끝점 계산
        const extraGapStart = 3; // 첫 점에서 더 띄우는 거리(px)
        const extraGapEnd = 7; // 레드닷 쪽에서 더 띄우는 거리(px)
        const startX = firstPixelX + (dx / dist) * (firstRadius + extraGapStart);
        const startY = firstPixelY + (dy / dist) * (firstRadius + extraGapStart);
        const endX = redPixelX - (dx / dist) * (redDotRadius + extraGapEnd);
        const endY = redPixelY - (dy / dist) * (redDotRadius + extraGapEnd);

        // 1. 선 그리기
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.strokeStyle = '#606976';
        ctx.lineWidth = 1;
        ctx.stroke();

        // 2. 화살표 그리기 (선 끝점에서)
        const drawArrow = (
            fromX: number,
            fromY: number,
            toX: number,
            toY: number,
            arrowLength = 4.5,
            arrowAngle = Math.PI / 4,
        ) => {
            const dx = toX - fromX;
            const dy = toY - fromY;
            const angle = Math.atan2(dy, dx);
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(toX, toY);
            ctx.lineTo(
                toX - arrowLength * Math.cos(angle - arrowAngle),
                toY - arrowLength * Math.sin(angle - arrowAngle),
            );
            ctx.moveTo(toX, toY);
            ctx.lineTo(
                toX - arrowLength * Math.cos(angle + arrowAngle),
                toY - arrowLength * Math.sin(angle + arrowAngle),
            );
            ctx.strokeStyle = '#606976';
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.restore();
        };

        drawArrow(startX, startY, endX, endY);

        ctx.restore();
    }

    if (averagePoint && props.month) {
        const labelY = averagePixelY - (radius + 15); // 점 위쪽에 표시, 15는 위 여백(px)
        ctx.save();
        ctx.font = '10px NotoSansKR, -apple-system, BlinkMacSystemFont, Roboto';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillStyle = '#606976';
        ctx.fillText(`${props.month}월 체성분 평균`, averagePixelX, labelY);
        ctx.restore();
    }

    ctx.restore();
};

onMounted(async () => {
    setInitData();
});
</script>

<style scoped lang="scss">
.cluster-change-polygon-graph {
    position: relative;
    width: 100%;
    height: auto;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    background-color: $grey-1;
    border-radius: 1.6rem;
    overflow: hidden;
}

.background-canvas,
.animation-canvas,
.class-area-canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: auto;
}

.background-canvas {
    z-index: 1;
}
.class-area-canvas {
    z-index: 1;
}
.polygon-canvas {
    z-index: 2;
}

.label-list {
    display: flex;
    justify-content: flex-end;
    padding-top: 0.4rem;

    .label-item-wrap {
        display: flex;
        flex-direction: row;
        align-items: center;
        width: auto;
        margin-right: 0.4rem;
        &:last-child {
            margin-right: 0;
        }
    }

    .label-item {
        display: flex;
        flex-direction: row;
        align-items: center;
        width: auto;
    }

    .label-color-chip {
        width: 1rem;
        height: 1rem;
        border-radius: 0.2rem;
        margin-right: 0.4rem;
        opacity: 0.5;
    }

    .label-text {
        display: inline-flex;
        flex-direction: row;
        align-items: center;
        font-size: 1rem;
        color: $grey-7;
    }
}
</style>
s
